import React, { useEffect, useState } from "react";
import { publicLink } from "@/lib/landerDomain";
import api from "@/lib/api";
import { Broadcast, WarningCircle } from "@phosphor-icons/react";

/**
 * Iter-74.2 — Landing-page traffic-source breakdown.
 *
 * Small read-only panel that answers:
 *   • For a Buyer: "which landing pages send leads to this buyer?"
 *   • For an Affiliate: "which landing pages does this vendor push to?"
 *
 * Props:
 *   scope         "buyer" | "affiliate" — decides which endpoint to hit.
 *   orgId         buyer_id (when scope="buyer") OR affiliate_id.
 *   testIdPrefix  data-testid base for the panel & rows.
 *
 * Auto-loads on mount. Silent when there are zero landing-page leads
 * in the window (both a fresh buyer and a webhook-only vendor should
 * see an empty panel with a helpful hint).
 */
export default function TrafficSourcesPanel({ scope, orgId, testIdPrefix = "traffic-sources" }) {
  const [days] = useState(30);
  const [state, setState] = useState({
    loading: true, error: null, rows: [], total: 0,
  });

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const url =
          scope === "affiliate"
            ? `/admin/affiliates/${orgId}/traffic-sources?days=${days}`
            : `/clients/${orgId}/traffic-sources?days=${days}`;
        const { data } = await api.get(url);
        if (cancelled) return;
        setState({
          loading: false, error: null,
          rows: data.landing_pages || [],
          total: data.total_submitted || 0,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          error: err.response?.data?.detail || "Failed to load traffic sources",
          rows: [], total: 0,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [scope, orgId, days]);

  const acceptancePct = (row) =>
    row.submitted ? Math.round((row.accepted / row.submitted) * 100) : 0;

  return (
    <div data-testid={testIdPrefix}>
      {state.loading && (
        <div className="label-mono text-zinc-500">Loading…</div>
      )}
      {state.error && (
        <div className="border border-amber-500 bg-amber-50 text-amber-900 p-3 text-xs flex items-center gap-2"
             data-testid={`${testIdPrefix}-error`}>
          <WarningCircle size={13} weight="fill" /> {state.error}
        </div>
      )}
      {!state.loading && !state.error && state.rows.length === 0 && (
        <div className="border border-zinc-300 bg-zinc-50 text-zinc-600 p-3 text-xs leading-relaxed"
             data-testid={`${testIdPrefix}-empty`}>
          No landing-page leads in the last {days} days. This is expected
          if the {scope === "buyer" ? "buyer just" : "affiliate just"} came
          online, or if leads are coming in via the API/webhook path
          only (webhook + CSV + manual leads never appear here — only
          traffic from a landing page does).
        </div>
      )}
      {!state.loading && !state.error && state.rows.length > 0 && (
        <div>
          <div className="label-mono text-zinc-500 mb-2 flex items-center gap-1">
            <Broadcast size={11} weight="bold" />
            Last {days} days · {state.total} landing-page lead{state.total === 1 ? "" : "s"}
          </div>
          <table className="w-full text-xs" data-testid={`${testIdPrefix}-table`}>
            <thead>
              <tr className="border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Landing page</th>
                <th className="text-right py-2 px-3">Submitted</th>
                <th className="text-right py-2 px-3">Accepted</th>
                <th className="text-right py-2 px-3">Rejected</th>
                <th className="text-right py-2 px-3">Acceptance</th>
                <th className="text-right py-2 px-3">Sandbox</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((r) => (
                <tr key={r.landing_page}
                    className="border-b border-zinc-200"
                    data-testid={`${testIdPrefix}-row-${r.landing_page}`}>
                  <td className="py-2 px-3 font-mono">
                    <a
                      href={publicLink(`/lp/${r.landing_page}`)}
                      target="_blank" rel="noopener noreferrer"
                      className="underline hover:text-emerald-700"
                    >
                      /lp/{r.landing_page}
                    </a>
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{r.submitted}</td>
                  <td className="py-2 px-3 text-right font-mono text-emerald-700">{r.accepted}</td>
                  <td className="py-2 px-3 text-right font-mono text-zinc-500">{r.rejected}</td>
                  <td className="py-2 px-3 text-right font-mono">
                    <span
                      className={`px-1.5 py-0.5 border font-bold ${
                        acceptancePct(r) >= 50
                          ? "border-emerald-700 bg-emerald-50 text-emerald-800"
                          : "border-zinc-400 bg-white text-zinc-600"
                      }`}
                    >
                      {acceptancePct(r)}%
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-zinc-400">{r.sandbox}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
